import {
  type BaseState,
  CONSOLE_VERSION,
  EXCEPTION_KEYWORD,
  HOST_QUERY_KEY,
  metaApiInstance,
  SERVICE_MIGRATION_STATUS,
  type ServiceAppState,
} from '@kc-console/context';
import { STATUS_CODE } from '@kc-console/core';

import { CookieKeyMap } from '../../constants/cookie';
import { commonGuardErrorHandler } from '../../utils/commonGuardErrorHandler';
import { parseCookies } from '../../utils/cookie/parseCookies';
import { getRequestUrlData } from '../../utils/getRequestUrlData';
import { parsePathname } from '../../utils/parsePathname';
import { getServerTraceHttpTimingOptions } from '../../utils/serverTrace/store';

import type { Guard } from '../../types';

/**
 * 사용자가 선택한 콘솔 버전과 진입하려는 서비스의 마이그레이션 상태에 따라 리다이렉트 또는 진입을 결정하는 라우트 가드
 * @description
 * - Local 개발 또는 beyond-console.* 또는 preview-console.* 도메인으로 접근 시, BD 에 대한 개발 또는 배포된 내용 확인 용도이기 때문에 그대로 진입 처리
 * - 서비스의 마이그레이션 상태가 CUTOVER 이면 BD 만 존재하기 때문에 그대로 진입
 * - 서비스의 마이그레이션 상태가 INCOMPLETE 이면 BD 로의 전환 자체가 되어있지 않은 상태이기 때문에 무조건 리다이렉트
 * - 서비스의 마이그레이션 상태가 COMPLETED 이면 BD 와 UR 이 공존하는 상태. 사용자의 설정 값에 따라 처리
 * @returns { Promise<GuardResponse> }
 */
export const determineConsoleVersionRouteGuard: Guard<ServiceAppState & BaseState> = async ({
  context,
  state,
  queryClient,
}) => {
  try {
    const { resolvedUrl } = context;
    const { urlObj, cookie } = getRequestUrlData(context);
    const { origin } = urlObj;

    // Local 개발 또는 beyond-console.* 또는 preview-console.* 도메인으로 접근 시, BD 에 대한 개발 또는 배포된 내용 확인 용도이기 때문에 그대로 진입 처리
    const isByPass =
      process.env.NODE_ENV === 'development' ||
      origin.includes(EXCEPTION_KEYWORD.BEYOND_CONSOLE) ||
      origin.includes(EXCEPTION_KEYWORD.PREVIEW_CONSOLE);
    // cookie token
    const cookies = parseCookies(cookie || '');
    const cookieToken = cookies[CookieKeyMap.TOKEN_KEY] || '';

    const { fetchUserPreference } = metaApiInstance({
      token: cookieToken,
      ...getServerTraceHttpTimingOptions(context.req, 'config'),
    });

    const { serviceKeyword } = parsePathname(resolvedUrl);

    const redirectObj = {
      redirect: {
        destination: `${origin}${resolvedUrl}`,
        permanent: false,
      },
    };

    const nextObj = { status: STATUS_CODE.OK };

    if (isByPass) {
      return nextObj;
    }

    const { consoleVersion, serviceMigrationStatus } = await queryClient.fetchQuery({
      queryKey: [HOST_QUERY_KEY.GET_USER_PREFERENCE, { serviceKeyword }],
      queryFn: () => fetchUserPreference({ serviceKeyword }),
    });

    if (
      resolvedUrl.startsWith(EXCEPTION_KEYWORD.BEYOND_COMPUTE_SERVICE) &&
      consoleVersion === CONSOLE_VERSION.UR
    ) {
      // BCS 를 위한 임시 로직이기 때문에 BCS 가 CUTOVER 상태가 되면 해당 if 만 제거될 예정
      // BD 상태에서 사용자가 BCS 관련 패스 어딘가로 이동을 하려고 하면서 콘솔 버전은 UR 로 선택한 경우
      // 공존하는 상태에서 UR 로의 패스 보정을 해서 보내줘야하기 때문에 해당 로직을 처리하기 위한 임시 구간
      const newResolvedUrl = resolvedUrl.replace(EXCEPTION_KEYWORD.BEYOND_COMPUTE_SERVICE, '');
      return {
        redirect: {
          destination: `${origin}${newResolvedUrl}`,
          permanent: false,
        },
      };
    }

    if (serviceMigrationStatus === SERVICE_MIGRATION_STATUS.INCOMPLETE) {
      // INCOMPLETE 인 경우, BD 로의 전환 자체가 되어있지 않은 상태이기 때문에 무조건 리다이렉트
      return redirectObj;
    } else if (serviceMigrationStatus === SERVICE_MIGRATION_STATUS.COMPLETED) {
      // BD 와 UR 이 공존하는 상태. 사용자의 설정 값에 따라 처리한다.
      return consoleVersion === CONSOLE_VERSION.BD ? nextObj : redirectObj;
    }

    // CUTOVER 인 경우, BD 로의 전환이 완료되고 UR 또한 없는 상태이기 때문에 무조건 BD 로 그대로 진입
    return nextObj;
  } catch (error) {
    return commonGuardErrorHandler({ context, state, error });
  }
};

determineConsoleVersionRouteGuard.displayName = 'determineConsoleVersionRouteGuard';
